import React, { useMemo } from 'react'

import {
  Flex,
  SegmentsMap,
  TruncateSegments,
  Skeleton,
} from '@sketch/components'
import {
  Header,
  Title,
  PreviewImage,
  PreviewImageEmpty,
  separatorWidth,
  SegmentSeparator,
  Subtitle,
  HeaderRight,
  TooltipContent,
  TooltipSegment,
  TooltipIcon,
  ActionLabel,
  ItemName,
  ItemPreviewWrapper,
  ProjectPreview,
} from './MoveStorageItemModalHeader.styles'

import { StorageItemFragment } from '@sketch/gql-types'
import { usePathToCurrent } from './getLocation'
import {
  useTreeState,
  TreeSearchFilter,
} from '../../../projects/modals/ProjectPickerContent'
import { useStorageItemsCount, useGetStorageItem } from '../../hooks/'
import { StorageItemIdentity } from '../../types'

const ITEM_PREVIEW_LIMIT = 3

const ItemLocation: React.FCC<{}> = () => {
  const treeState = useTreeState()
  const segments = usePathToCurrent()

  const directParentId = treeState.currentIds.currentId

  const segmentsMap: SegmentsMap = new Map()
  for (const entry of segments) {
    segmentsMap.set(entry.id, {
      id: entry.id,
      name: entry.name,
      node: (
        <Subtitle bold={entry.id === directParentId}>{entry.name}</Subtitle>
      ),
    })
  }

  return (
    <Subtitle>
      <TruncateSegments
        segments={segmentsMap}
        separator={{ element: <SegmentSeparator />, width: separatorWidth }}
        ellipsis={{ element: <span>…</span>, width: separatorWidth }}
        renderTooltip={segmentIds => {
          return (
            <TooltipContent>
              {segmentIds.map((id, index) => (
                <TooltipSegment key={id} depth={index}>
                  <TooltipIcon />
                  {segmentsMap.get(id)?.name}
                </TooltipSegment>
              ))}
            </TooltipContent>
          )
        }}
      />
      {
        // reduce jitter when loading
        segments.length === 0 && <>&nbsp;</>
      }
    </Subtitle>
  )
}

interface ItemPreviewProps {
  item?: StorageItemFragment
}

export const ItemPreview = (props: ItemPreviewProps) => {
  const { item } = props

  if (!item) {
    return <PreviewImageEmpty data-testid="move-storage-item-placeholder" />
  }

  if (item.__typename === 'Share') {
    const thumbnails = item?.version?.document?.previewFile?.thumbnails
    const image = thumbnails ? thumbnails[0]?.url : undefined

    if (!image) {
      return <PreviewImageEmpty data-testid="move-storage-item-placeholder" />
    }

    return <PreviewImage alt={item?.name ?? ''} src={image} />
  }

  if (item.__typename === 'Project') {
    return <ProjectPreview nestedProject={item} />
  }

  return <PreviewImageEmpty data-testid="move-storage-item-placeholder" />
}

interface ItemPreviewWithFetchProps {
  item: StorageItemIdentity
}

const ItemPreviewWithFetch = (props: ItemPreviewWithFetchProps) => {
  const { item } = props
  const storageItem = useGetStorageItem(item)

  return <ItemPreview item={storageItem} />
}

interface MoveStorageItemModalHeaderProps {
  actionLabel: string
  item: StorageItemIdentity
}

export const MoveStorageItemModalHeader = (
  props: MoveStorageItemModalHeaderProps
) => {
  const { item, actionLabel } = props

  const storageItem = useGetStorageItem(item)

  return (
    <Header>
      <Flex flexDirection="column">
        <Flex alignItems="center" mb={3}>
          <ItemPreview key={item.identifier} {...props} item={storageItem} />
          <HeaderRight>
            <Title>
              <ActionLabel>{actionLabel}:</ActionLabel>{' '}
              <ItemName>{storageItem?.name ?? <>&hellip;</>}</ItemName>
            </Title>
            <ItemLocation />
          </HeaderRight>
        </Flex>
        <TreeSearchFilter />
      </Flex>
    </Header>
  )
}

interface MoveStorageItemsModalHeaderProps {
  items: StorageItemIdentity[]
  actionLabel: string
}

export const MoveStorageItemsModalHeader = (
  props: MoveStorageItemsModalHeaderProps
) => {
  const { items, actionLabel } = props
  const { message } = useStorageItemsCount(items)

  const safeguardedItems = useMemo(
    () => (items || []).slice(0, ITEM_PREVIEW_LIMIT),
    [items]
  )

  return (
    <Header>
      <Flex flexDirection="column">
        <Flex alignItems="center" mb={3}>
          <ItemPreviewWrapper>
            {safeguardedItems.map(item => (
              <ItemPreviewWithFetch key={item.identifier} item={item} />
            ))}
          </ItemPreviewWrapper>

          <HeaderRight>
            <Title>
              <ActionLabel>{actionLabel} Selection</ActionLabel>{' '}
            </Title>
            <Subtitle>{message}</Subtitle>
          </HeaderRight>
        </Flex>
        <TreeSearchFilter />
      </Flex>
    </Header>
  )
}

export interface MoveStorageItemModalHeaderLoadingProps {
  actionLabel: string
}
export const MoveStorageItemModalHeaderLoading = (
  props: MoveStorageItemModalHeaderLoadingProps
) => {
  const { actionLabel } = props
  return (
    <Header>
      <Flex flexDirection="column">
        <Flex alignItems="center" mb={4}>
          <PreviewImageEmpty />

          <HeaderRight>
            <Title>
              <ActionLabel>{actionLabel}:</ActionLabel>
              <Skeleton width="30%" height="20px" />
            </Title>
            <Subtitle>
              <Skeleton width="30%" height="13px" />
            </Subtitle>
          </HeaderRight>
        </Flex>
        <TreeSearchFilter />
      </Flex>
    </Header>
  )
}
